// Auto-layout: every visible topic gets a box (x, y, w, h in map units, the
// central topic centred on 0,0), and every parent-child pair an edge. Six
// layouts: balanced (both sides), right, left, down (org chart), up, tree
// (indented, like an outline). A branch takes a band as tall (or wide) as its
// children need, so nothing ever overlaps. Collapsed branches take no room.
import { topicStyle, themeOf, PALETTE, colorHex } from './theme.js';
import { taskProgress, descendants } from './model.js';
import { graphOpts, placeNew, settle } from './graph.js';

// ---- text -------------------------------------------------------------------------------
let ctx = null;
const widths = new Map();
export function measureText(text, font) {
  const key = `${font}|${text}`;
  const hit = widths.get(key);
  if (hit != null) return hit;
  if (ctx === null) {
    try { const c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(1, 1) : document.createElement('canvas'); ctx = c.getContext('2d') || false; } catch (_) { ctx = false; }
  }
  let w;
  if (ctx) { ctx.font = font; w = ctx.measureText(text).width; }
  else { const px = Number((font.match(/(\d+)px/) || [])[1]) || 14; w = text.length * px * 0.56; } // Node (tests): an estimate
  if (widths.size > 20000) widths.clear();
  widths.set(key, w);
  return w;
}

/** Break text into lines no wider than maxW (explicit line breaks kept; a long word breaks by letter). */
export function wrapText(text, font, maxW) {
  const out = [];
  for (const para of String(text).split('\n')) {
    const words = para.split(/ +/);
    let line = '';
    for (let word of words) {
      const tryLine = line ? `${line} ${word}` : word;
      if (measureText(tryLine, font) <= maxW) { line = tryLine; continue; }
      if (line) { out.push(line); line = ''; }
      while (measureText(word, font) > maxW && word.length > 1) {
        let k = word.length - 1;
        while (k > 1 && measureText(word.slice(0, k), font) > maxW) k--;
        out.push(word.slice(0, k));
        word = word.slice(k);
      }
      line = word;
    }
    out.push(line);
  }
  return out;
}

export const PLACEHOLDER = 'Topic';
const SPACING = { compact: 0.7, normal: 1, roomy: 1.45 };

/** Each topic's branch colour: first-level topics take the palette in turn, the rest inherit. */
export function branchColors(map) {
  const out = new Map();
  const walk = (nd, color) => { const c = nd.color ? colorHex(nd.color) || color : color; out.set(nd.id, c); for (const k of nd.children) walk(k, c); };
  out.set(map.root.id, map.root.color ? colorHex(map.root.color) : null);
  map.root.children.forEach((k, i) => walk(k, PALETTE[i % PALETTE.length][1]));
  map.floating.forEach((f, i) => walk(f, PALETTE[(i * 3 + 5) % PALETTE.length][1]));
  return out;
}

/** The size and text lines of one topic. `bubble`: 'bubbles' or 'dots' on a bubble map. */
function sizeBox(nd, depth, color, T, isFT, bubble = null, degree = 1) {
  if (bubble === 'dots') return dotBox(nd, depth, color, T, isFT, degree);
  const st = topicStyle(nd, depth, color || T.root, T, isFT, bubble);
  const text = nd.text || '';
  const lines = text ? wrapText(text, st.font, st.maxW) : [''];
  let textW = Math.max(...lines.map((l) => measureText(l, st.font)));
  if (!text) textW = measureText(PLACEHOLDER, st.font);
  const icon = Math.round(st.fontSize * 0.9);
  const left = nd.task ? icon + 6 : 0;
  const prog = taskProgress(nd);
  const progText = prog ? `${prog.done}/${prog.total}` : '';
  const progFont = `600 ${Math.round(st.fontSize * 0.78)}px ${st.font.split('px ')[1]}`;
  let right = 0;
  if (nd.link) right += icon + 6;
  if (nd.note) right += icon + 6;
  if (progText) right += measureText(progText, progFont) + 12;
  let w = st.padX * 2 + left + textW + right;
  let h = st.padY * 2 + lines.length * st.lineH;
  if (st.shape === 'ellipse') { w += st.padX * 1.1 + 8; h += st.padY * 1.3; }
  if (st.shape === 'pill') w += h * 0.25;
  w = Math.max(Math.ceil(w), 28);
  h = Math.ceil(h);
  if (bubble) {
    // the smallest circle round the text block, with room to breathe
    const lvl = st.lvl;
    const r = Math.ceil(Math.max(lvl === 0 ? 46 : lvl === 1 ? 34 : 26, Math.hypot((left + textW + right) / 2, (lines.length * st.lineH) / 2) + (lvl === 0 ? 16 : 11)));
    return { st, lines, textW, left, right, icon, progText, progFont, w: r * 2, h: r * 2, circle: true };
  }
  return { st, lines, textW, left, right, icon, progText, progFont, w, h };
}

/** A dot (Obsidian style): its size grows with its connections; the label sits under it. */
function dotBox(nd, depth, color, T, isFT, degree) {
  const st = topicStyle(nd, depth, color || T.root, T, isFT, 'dots');
  const lvl = st.lvl;
  const fontSize = lvl === 0 ? 15 : lvl === 1 ? 13 : 12;
  const font = `${nd.italic ? 'italic ' : ''}${nd.bold || lvl === 0 ? 700 : lvl === 1 ? 600 : 500} ${fontSize}px ${st.font.split('px ')[1]}`;
  const text = nd.text || '';
  const lines = text ? wrapText(text, font, 150) : [''];
  const textW = text ? Math.max(...lines.map((l) => measureText(l, font))) : measureText(PLACEHOLDER, font);
  const k = { s: 0.8, m: 1, l: 1.3, xl: 1.7 }[nd.size] || 1;
  const r = Math.round((5 + 2.4 * Math.sqrt(degree) + (lvl === 0 ? 5 : 0)) * k);
  const lineH = Math.round(fontSize * 1.3);
  return { st: { ...st, fontSize, font, lineH, weight: nd.bold || lvl === 0 ? 700 : lvl === 1 ? 600 : 500 }, lines, textW, left: 0, right: 0, icon: Math.round(fontSize * 0.9), progText: '', progFont: font, w: r * 2, h: r * 2, circle: true, dot: true, lab: { w: textW + (nd.task ? 18 : 0), h: lines.length * lineH + 4 } };
}

/**
 * Lay out the map. `focus`: show only that topic's branch, laid out as if it
 * were the central topic. Returns { boxes: Map(id -> box), edges, rels, bounds }.
 */
export function layoutMap(map, { focus = null, local = null, positions = null } = {}) {
  const T = themeOf(map);
  const k = SPACING[map.spacing] || 1;
  const colors = branchColors(map);
  const boxes = new Map();
  const edges = [];
  const isBubble = map.layout === 'bubble';
  const G = isBubble ? graphOpts(map) : null;
  // dots grow with their connections: count them first
  const degree = new Map();
  if (G?.style === 'dots') {
    const bump = (id) => degree.set(id, (degree.get(id) || 0) + 1);
    const walk = (nd) => { for (const c of nd.children) { bump(nd.id); bump(c.id); walk(c); } };
    walk(map.root); map.floating.forEach(walk);
    for (const r of map.rels) { bump(r.from); bump(r.to); }
  }

  const build = (nd, depth, isFT, parent) => {
    const color = colors.get(nd.id);
    const s = sizeBox(nd, depth, color, T, isFT, G ? G.style : null, degree.get(nd.id) || 0);
    const b = { id: nd.id, nd, depth, isFT, color, parent: parent ? parent.id : null, x: 0, y: 0, ...s, kids: [], hidden: 0, dir: 'right' };
    boxes.set(nd.id, b);
    if (nd.collapsed && nd.children.length) b.hidden = descendants(nd);
    else for (const c of nd.children) { const kb = build(c, depth + 1, false, b); b.kids.push(kb); edges.push({ from: b.id, to: kb.id }); }
    return b;
  };

  const hRoot = 64 * k; const hGap = 36 * k; const v1 = 16 * k; const vGap = 9 * k;
  const sibV = (b) => (b.depth === 0 ? v1 : vGap);
  const levelH = (b) => (b.depth === 0 ? hRoot : hGap);
  const band = new Map();
  const bandH = (b) => {
    if (band.has(b)) return band.get(b);
    const block = b.kids.reduce((a, c) => a + bandH(c), 0) + sibV(b) * Math.max(0, b.kids.length - 1);
    const v = Math.max(b.h, block);
    band.set(b, v);
    return v;
  };
  const blockH = (list, b) => list.reduce((a, c) => a + bandH(c), 0) + sibV(b) * Math.max(0, list.length - 1);
  // x is the edge nearest the parent: the left edge going right, the right edge going left
  const placeH = (b, x, yTop, dir) => {
    const bh = bandH(b);
    b.dir = dir > 0 ? 'right' : 'left';
    b.x = dir > 0 ? x : x - b.w;
    b.y = yTop + bh / 2 - b.h / 2;
    const cx = dir > 0 ? b.x + b.w + levelH(b) : b.x - levelH(b);
    let y = yTop + (bh - blockH(b.kids, b)) / 2;
    for (const c of b.kids) { placeH(c, cx, y, dir); y += bandH(c) + sibV(b); }
  };

  const sibH = (b) => (b.depth === 0 ? 28 * k : 16 * k);
  const levelV = (b) => (b.depth === 0 ? 56 * k : 40 * k);
  const bandWm = new Map();
  const bandW = (b) => {
    if (bandWm.has(b)) return bandWm.get(b);
    const block = b.kids.reduce((a, c) => a + bandW(c), 0) + sibH(b) * Math.max(0, b.kids.length - 1);
    const v = Math.max(b.w, block);
    bandWm.set(b, v);
    return v;
  };
  const placeV = (b, xLeft, y, dir) => {
    const bw = bandW(b);
    b.dir = dir > 0 ? 'down' : 'up';
    b.x = xLeft + bw / 2 - b.w / 2;
    b.y = dir > 0 ? y : y - b.h;
    const block = b.kids.reduce((a, c) => a + bandW(c), 0) + sibH(b) * Math.max(0, b.kids.length - 1);
    let x = xLeft + (bw - block) / 2;
    const cy = dir > 0 ? b.y + b.h + levelV(b) : b.y - levelV(b);
    for (const c of b.kids) { placeV(c, x, cy, dir); x += bandW(c) + sibH(b); }
  };

  const indent = 28 * Math.max(0.8, k); const rowGap = 8 * k;
  const placeT = (b, x, y) => {
    b.dir = 'tree';
    b.x = x; b.y = y;
    let cur = y + b.h + rowGap * (b.depth === 0 ? 2 : 1);
    for (const c of b.kids) cur = placeT(c, x + indent + (b.depth === 0 ? 8 : 0), cur);
    return cur + (b.kids.length ? rowGap * 0.5 : 0);
  };

  /** Lay out one tree with its top box centred on 0,0 (balanced splits the first level). */
  const placeTree = (top, layout) => {
    if (layout === 'balanced') {
      const R = []; const L = [];
      const autos = top.kids.filter((c) => c.nd.side !== 'left' && c.nd.side !== 'right');
      const fixedR = top.kids.filter((c) => c.nd.side === 'right').length;
      const want = Math.ceil(top.kids.length / 2);
      let r = fixedR;
      for (const c of top.kids) {
        if (c.nd.side === 'left') L.push(c);
        else if (c.nd.side === 'right') R.push(c);
        else if (r < want && autos.length) { R.push(c); r++; } else L.push(c);
      }
      top.dir = 'both';
      top.x = -top.w / 2; top.y = -top.h / 2;
      let y = -blockH(R, top) / 2;
      for (const c of R) { placeH(c, top.x + top.w + hRoot, y, 1); y += bandH(c) + v1; }
      y = -blockH(L, top) / 2;
      for (const c of L) { placeH(c, top.x - hRoot, y, -1); y += bandH(c) + v1; }
      top.sides = { R: R.map((c) => c.id), L: L.map((c) => c.id) };
    } else if (layout === 'right') placeH(top, -top.w / 2, -bandH(top) / 2, 1);
    else if (layout === 'left') placeH(top, top.w / 2, -bandH(top) / 2, -1);
    else if (layout === 'down') placeV(top, -bandW(top) / 2, -top.h / 2, 1);
    else if (layout === 'up') placeV(top, -bandW(top) / 2, top.h / 2, -1);
    else placeT(top, -top.w / 2, -top.h / 2);
  };
  const shift = (b, dx, dy) => { b.x += dx; b.y += dy; for (const c of b.kids) shift(c, dx, dy); };

  let focusOk = false;
  let localOk = false;
  const tops = [];
  if (focus) {
    const find = (nd) => (nd.id === focus ? nd : nd.children.reduce((a, c) => a || find(c), null));
    const nd = [map.root, ...map.floating].reduce((a, t) => a || find(t), null);
    if (nd) {
      focusOk = true;
      tops.push(build(nd, 0, false, null));
      if (!isBubble) placeTree(tops[0], map.layout);
    }
  }
  if (!focusOk) {
    const root = build(map.root, 0, false, null);
    tops.push(root);
    if (!isBubble) placeTree(root, map.layout);
    const fl = map.layout === 'balanced' ? 'right' : map.layout;
    for (const f of map.floating) {
      const top = build(f, 0, true, null);
      tops.push(top);
      if (!isBubble) { placeTree(top, fl); shift(top, (f.x || 0) - top.x, (f.y || 0) - top.y); }
    }
  }
  // a local graph: only the topics within `depth` links (branches and relationships) of one topic
  if (isBubble && local && boxes.has(local.id)) {
    localOk = true;
    const nbr = new Map();
    const link = (a, b) => { if (!boxes.has(a) || !boxes.has(b)) return; (nbr.get(a) || nbr.set(a, []).get(a)).push(b); (nbr.get(b) || nbr.set(b, []).get(b)).push(a); };
    for (const e of edges) link(e.from, e.to);
    for (const r of map.rels) link(r.from, r.to);
    const keep = new Set([local.id]);
    let ring = [local.id];
    for (let d = 0; d < (local.depth || 1); d++) { const next = []; for (const id of ring) for (const o of nbr.get(id) || []) if (!keep.has(o)) { keep.add(o); next.push(o); } ring = next; }
    for (const id of [...boxes.keys()]) if (!keep.has(id)) boxes.delete(id);
    for (let i = edges.length - 1; i >= 0; i--) if (!keep.has(edges[i].from) || !keep.has(edges[i].to)) edges.splice(i, 1);
    for (const b of boxes.values()) { if (b.parent && !keep.has(b.parent)) b.parent = null; b.kids = b.kids.filter((c) => keep.has(c.id)); }
  }
  let links = null;
  if (isBubble) links = placeBubbles(map, boxes, edges, G, k, positions);

  const rels = (map.rels || []).filter((r) => boxes.has(r.from) && boxes.has(r.to));
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  for (const b of boxes.values()) {
    x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y); x1 = Math.max(x1, b.x + b.w); y1 = Math.max(y1, b.y + b.h);
    if (b.hidden) { const p = badgePos(b); x0 = Math.min(x0, p.x - 14); x1 = Math.max(x1, p.x + 14); y0 = Math.min(y0, p.y - 12); y1 = Math.max(y1, p.y + 12); }
    if (b.lab) { const cx = b.x + b.w / 2; x0 = Math.min(x0, cx - b.lab.w / 2 - 4); x1 = Math.max(x1, cx + b.lab.w / 2 + 4); y1 = Math.max(y1, b.y + b.h + 4 + b.lab.h); }
  }
  if (!boxes.size) { x0 = y0 = -50; x1 = y1 = 50; }
  return { boxes, edges, rels, links, bounds: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }, theme: T, focus: focusOk ? focus : null, local: localOk ? local : null, bubble: isBubble ? G.style : null };
}

/**
 * A bubble map's places: each topic's saved centre (gx, gy), the editor's live
 * positions, or a new place near its parent. Without live positions (the file
 * export, Node) the physics runs to rest here; in the editor it runs live.
 * Returns the springs (for the editor's simulation).
 */
function placeBubbles(map, boxes, edges, G, k, positions) {
  const gap = (G.style === 'dots' ? 42 : 30) * k * (G.distance || 1);
  const nodes = []; const byId = new Map();
  let anySaved = false;
  for (const b of boxes.values()) {
    const n = { id: b.id, r: b.w / 2, x: 0, y: 0, vx: 0, vy: 0, fixed: !!b.nd.pin, placed: false, b };
    const pos = positions?.get(b.id) || (b.nd.gx != null && b.nd.gy != null ? { x: Number(b.nd.gx), y: Number(b.nd.gy) } : null);
    if (pos) { n.x = pos.x; n.y = pos.y; n.placed = true; anySaved = true; }
    nodes.push(n); byId.set(b.id, n);
  }
  // tops without a place: the central topic in the middle, floating topics where they were put
  for (const n of nodes) if (!n.placed && !n.b.parent) {
    if (n.b.isFT) { n.x = (n.b.nd.x || 0) + n.r; n.y = (n.b.nd.y || 0) + n.r; } else { n.x = 0; n.y = 0; }
    n.placed = true;
  }
  const fresh = nodes.filter((n) => !n.placed).map((n) => n.id);
  placeNew(nodes, byId, (id) => boxes.get(id)?.parent || null, (id) => (boxes.get(id)?.kids || []).map((c) => c.id), gap);
  const index = new Map(nodes.map((n, i) => [n.id, i]));
  const links = [];
  for (const e of edges) { const a = index.get(e.from); const b = index.get(e.to); if (a != null && b != null) links.push({ a, b, len: nodes[a].r + nodes[b].r + gap, k: 1 }); }
  for (const r of map.rels) { const a = index.get(r.from); const b = index.get(r.to); if (a != null && b != null) links.push({ a, b, len: nodes[a].r + nodes[b].r + gap * 1.8, k: 0.25 }); }
  if (!positions) {
    if (!anySaved) settle(nodes, links, { f: G });
    else if (fresh.length) {
      // new topics in a saved map: settle them among the others, which stay put
      const was = nodes.map((n) => n.fixed);
      for (const n of nodes) if (!fresh.includes(n.id)) n.fixed = true;
      settle(nodes, links, { ticks: 120, alpha: 0.5, f: G });
      nodes.forEach((n, i) => { n.fixed = was[i]; });
    }
  }
  for (const n of nodes) { const b = n.b; b.x = n.x - n.r; b.y = n.y - n.r; b.dir = 'bubble'; }
  return { nodes: nodes.map((n) => n.id), links, fresh, anySaved };
}

/** Where a topic's collapse badge (or its expand toggle) sits: just off the side its branch grows from. */
export function badgePos(b) {
  const cy = b.y + b.h / 2; const cx = b.x + b.w / 2;
  const line = b.st.mode === 'line';
  switch (b.dir) {
    case 'bubble': { const r = b.w / 2; return b.dot ? { x: cx + r + 12, y: cy } : { x: cx + r * 0.74, y: cy + r * 0.74 }; } // a dot's label is under it: keep to its right
    case 'left': return { x: b.x - 13, y: line ? b.y + b.h : cy };
    case 'down': return { x: cx, y: b.y + b.h + 13 };
    case 'up': return { x: cx, y: b.y - 13 };
    case 'tree': return { x: b.x + 14, y: b.y + b.h + 11 };
    case 'both': return { x: b.x + b.w + 13, y: cy };
    default: return { x: b.x + b.w + 13, y: line ? b.y + b.h : cy };
  }
}
